import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { Observable, Subscription, filter } from 'rxjs';
import { TableLazyLoadEvent } from 'primeng/table';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import * as DeviceBoardusersActions from 'src/app/store/device-boardusers/device-boardusers.actions';
import { pageSortChange } from 'src/app/services/table';
import { Tooltip } from 'primeng/tooltip';
import { selectBoardUsersTaskRunning, selectCurrentDeviceStatus } from 'src/app/store/device/device.selector';
import { BoardUsersListsRespData } from 'src/app/model/api/data-rest';
import { DeviceStatus, isDeviceUnavailable } from 'src/app/model/device-status';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';
import { BoardUsersImportListDialogComponent } from '../board-users-import-list-dialog/board-users-import-list.dialog.component';
import { ConfirmationService } from 'primeng/api';
import { BoardUsersExportListDialogComponent } from '../board-users-export-list-dialog/board-users-export-list.dialog.component';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { selectBoardUsersExported, selectBoardUsersExporting, selectBoardUsersListsRespData, selectBoardUsersListsRespDataLoading } from 'src/app/store/device-boardusers/device-boardusers.selector';
import { BoardUsersList } from 'src/app/model/board-user';
import { BOARD_USERS_LISTS_PAGE_DEFAULT_SIZE, BOARD_USERS_LISTS_DEFAULT_SORT_COLUMN, BOARD_USERS_LISTS_DEFAULT_SORT_COLUMN_DIRECTION, BOARD_USERS_LISTS_PAGE_SIZE_OPTIONS } from 'src/app/conf/board-users';
import { CommonCustomConfirmDialogComponent } from 'src/app/modules/shared/components/common-custom-confirm-dialog/common-custom-confirm.dialog.component';

@Component({
	selector: 'app-board-users-lists',
	templateUrl: './board-users-lists.component.html',
	providers: []
})

export class BoardUsersListsComponent implements OnInit, OnDestroy {

	boardUsersList: BoardUsersList[] = [];
	boardUsersListsResp: BoardUsersListsRespData;
	boardUsersListsResp$: Observable<BoardUsersListsRespData | undefined>;
	boardUsersListsRespSub: Subscription | undefined;

	buListsRespLoading: boolean;
	buListsRespLoading$: Observable<boolean | undefined>;
	buListsRespLoadingSub: Subscription | undefined;

	paging: PaginationParams = {
		size: BOARD_USERS_LISTS_PAGE_DEFAULT_SIZE,
		page: 1,
		sort: BOARD_USERS_LISTS_DEFAULT_SORT_COLUMN,
		order: BOARD_USERS_LISTS_DEFAULT_SORT_COLUMN_DIRECTION,
	};
	pageSizeOptions = BOARD_USERS_LISTS_PAGE_SIZE_OPTIONS;
	pageSize = BOARD_USERS_LISTS_PAGE_DEFAULT_SIZE;
	resultLength: number;

	taskRunning: boolean;
	taskRunning$: Observable<boolean>;
	taskRunningSub: Subscription | undefined;

	offline: boolean;
	offline$: Observable<DeviceStatus>;
	offlineSub: Subscription | undefined;

	exporting: boolean = false;
	exporting$: Observable<boolean | undefined>;
	exportingSub: Subscription;
	exported$: Observable<boolean | undefined>;
	exportedSub: Subscription;

	totalRecords = 0;

	sort: any;

	// design
	screenWidth: number;

	ref: DynamicDialogRef | undefined;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	@ViewChild(Tooltip) tooltip!: Tooltip;

	constructor(
		private store: Store<AppState>,
		private translate: TranslateService,
		public dialogService: DialogService,
		public confirmationService: ConfirmationService,
	) {
		this.initSelectors();
	}

	ngOnInit(): void {
		this.subscribeAll();
		this.getBoardUserList();

		this.onResize();
	}

	initSelectors(): void {
		this.buListsRespLoading$ = this.store.select(selectBoardUsersListsRespDataLoading);
		this.boardUsersListsResp$ = this.store.select(selectBoardUsersListsRespData);
		this.taskRunning$ = this.store.select(selectBoardUsersTaskRunning);
		this.offline$ = this.store.select(selectCurrentDeviceStatus);
		this.exporting$ = this.store.select(selectBoardUsersExporting);
		this.exported$ = this.store.select(selectBoardUsersExported);
	}

	showHideTooltip() {
		this.tooltip.activate();
	}

	subscribeAll(): void {
		this.buListsRespLoadingSub = this.buListsRespLoading$?.subscribe(
			(buListsRespLoading) => {
				this.buListsRespLoading = buListsRespLoading;
			}
		);
		this.boardUsersListsRespSub = this.boardUsersListsResp$?.subscribe(
			(boardUsersListsResp) => {
				if (boardUsersListsResp) {
					this.boardUsersListsResp = boardUsersListsResp;
					this.boardUsersList = boardUsersListsResp.Lists;
					const paging = this.boardUsersListsResp.Paging;
					if (paging) {
						this.totalRecords = paging.TotalCount;
					}
				}
			}
		);
		this.offlineSub = this.offline$?.pipe(filter(d => !!d))
			.subscribe(
				(deviceStatus) => {
					this.offline = isDeviceUnavailable(deviceStatus);
				}
			);
		this.taskRunningSub = this.taskRunning$.subscribe(
			(taskRunning) => {
				this.taskRunning = taskRunning;
			}
		);
		this.exportingSub = this.exporting$?.subscribe(
			(exporting) => {
				this.exporting = exporting;
			}
		);
		this.exportedSub = this.exported$?.pipe(filter(res => !!res)).subscribe(
			() => {
				this.paging = {
					size: BOARD_USERS_LISTS_PAGE_DEFAULT_SIZE,
					page: 1,
					sort: BOARD_USERS_LISTS_DEFAULT_SORT_COLUMN,
					order: BOARD_USERS_LISTS_DEFAULT_SORT_COLUMN_DIRECTION,
				};
			}
		);
	}

	ngOnDestroy(): void {
		this.buListsRespLoadingSub?.unsubscribe();
		this.boardUsersListsRespSub?.unsubscribe();
		this.exportedSub?.unsubscribe();
		this.exportingSub?.unsubscribe();
	}

	getBoardUserList(): void {
		this.store.dispatch(DeviceBoardusersActions.getBoardusersLists({ paging: this.paging }));
	}

	loadLists(event: TableLazyLoadEvent) {
		this.paging = pageSortChange(this.paging, event);

		this.getBoardUserList();
	}

	importList(boardUserList: BoardUsersList): void {
		this.store.dispatch(DeviceBoardusersActions.resetImportBoarduserListDialog());
		this.ref = this.dialogService.open(BoardUsersImportListDialogComponent, {
			header: this.translate.instant('IMPORT_LIST'),
			width: '70vw',
			breakpoints: { '1120px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				boardUserList: boardUserList,
				paging: this.paging,
			}
		});
	}

	editList(boardUserList: BoardUsersList): void {
		this.store.dispatch(DeviceBoardusersActions.resetBoarduserListDialog());
		this.ref = this.dialogService.open(BoardUsersExportListDialogComponent, {
			header: this.translate.instant('EDIT_NAME') + ' ' + this.translate.instant('LIST').toLowerCase(),
			width: '70vw',
			breakpoints: { '1120px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				boardUserList: boardUserList,
				versionLists: this.boardUsersListsResp ? this.boardUsersListsResp.Version : '',
				listUpdating: true,
				paging: this.paging
			}
		});
	}

	deleteList(boardUserList: BoardUsersList): void {
		const deleteListConfDialog = this.dialogService.open(CommonCustomConfirmDialogComponent, {
			header: this.translate.instant('DELETE_HELP_ROW'),
			contentStyle: { overflow: 'auto' },
			data: {
				icon: 'cm-alert',
				row1: 'DELETE_HELP_ROW_WARNING',
				classAcceptButton: 'p-button-danger',
			}
		});
		deleteListConfDialog.onClose.subscribe((accepted: boolean) => {
			if (accepted) {
				this.store.dispatch(DeviceBoardusersActions.deleteBoarduserList({
					listId: boardUserList.Id,
					version: this.boardUsersListsResp ? this.boardUsersListsResp.Version : '',
					paging: this.paging
				}));
			}
		});
	}

	downloadUrl(url: string, fileName: string) {
		const a: any = document.createElement('a');
		a.href = url;
		a.download = fileName;
		document.body.appendChild(a);
		a.style = 'display: none';
		a.click();
		a.remove();
	}

	// Design
	@HostListener('window:resize')
	onResize(): void {
		this.screenWidth = window.innerWidth;
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}
}
